import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Container,
  Toolbar,
  Alert,
  AlertTitle
} from '@mui/material';
import 'leaflet/dist/leaflet.css'; // Import Leaflet CSS
import theme from './theme';
import Header from './components/Header';
import CaliforniaMap from './components/CaliforniaMap';
import StationSelectorModal from './components/StationSelectorModal';
import DataTableModal from './components/DataTableModal';

// Import services
import { apiServices, dataServices } from './services';
import { matchesRefStatusFilter, matchesFlowStatusFilter } from './services/helperServices';

function App() {
  const [stations, setStations] = useState([]);
  const [counties, setCounties] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countyStations, setCountyStations] = useState([]);
  const [mapLocations, setMapLocations] = useState([]); // Grouped locations for map display
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedGeometry, setSelectedGeometry] = useState(null);
  const [zoomToStation, setZoomToStation] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedStationId, setSelectedStationId] = useState('');
  const [stationInfoLoading, setStationInfoLoading] = useState(false);
  const [selectedStationData, setSelectedStationData] = useState(null);
  const [countiesLoading, setCountiesLoading] = useState(false);
  const [stationsLoading, setStationsLoading] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [zoomToCaliforniaTrigger, setZoomToCaliforniaTrigger] = useState(0);
  const [refStatusFilter, setRefStatusFilter] = useState('');
  const [flowStatusFilter, setFlowStatusFilter] = useState('');

  // Filter map locations by ref/flow status
  const filteredMapLocations = mapLocations.filter(loc =>
    matchesRefStatusFilter(loc.refstatus_final, refStatusFilter) &&
    matchesFlowStatusFilter(loc.flowstatus_final, flowStatusFilter)
  );

  // Fetch initial data on load
  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch counties and regions in parallel
      const [countiesData, regionsData] = await Promise.all([
        apiServices.fetchCounties(),
        apiServices.fetchRegions()
      ]);
      
      if (countiesData.success && countiesData.counties && countiesData.counties.length > 0) {
        setCounties(countiesData.counties);
      } else if (!countiesData.success) {
        throw new Error(countiesData.error || 'Failed to fetch counties');
      }
      
      if (regionsData.success && regionsData.regions && regionsData.regions.length > 0) {
        setRegions(regionsData.regions);
        // Auto-select the first region by default
        const firstRegion = regionsData.regions[0];
        setSelectedRegion(firstRegion);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError(error.message);
      setCounties([]);
      setRegions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch stations for selected county
  const fetchStationsForSelectedCounty = async (county) => {
    try {
      setStationsLoading(true);
      
      const data = await apiServices.fetchStationsForCounty(county);
      
      
      if (data.success) {
        // Use 'stations' for dropdown (individual stations)
        setCountyStations(data.stations || []);
        // Use 'locations' for map display (grouped by masterid)
        setMapLocations(data.locations || data.stations || []);
      } else {
        setCountyStations([]);
        setMapLocations([]);
      }
    } catch (error) {
      console.error(`Error fetching stations for county ${county}:`, error);
      setCountyStations([]);
      setMapLocations([]);
    } finally {
      setStationsLoading(false);
    }
  };

  // Fetch stations for selected region
  const fetchStationsForSelectedRegion = async (region) => {
    try {
      setStationsLoading(true);
      
      const data = await apiServices.fetchStationsForRegion(region);
      
      
      if (data.success) {
        // Use 'stations' for dropdown (individual stations)
        setCountyStations(data.stations || []);
        // Use 'locations' for map display (grouped by masterid)
        setMapLocations(data.locations || data.stations || []);
      } else {
        setCountyStations([]);
        setMapLocations([]);
      }
    } catch (error) {
      console.error(`Error fetching stations for region ${region}:`, error);
      setCountyStations([]);
      setMapLocations([]);
    } finally {
      setStationsLoading(false);
    }
  };

  useEffect(() => {
    // Fetch initial data on mount
    fetchInitialData();
  }, []);

  useEffect(() => {
    // Fetch stations when region is selected
    if (selectedRegion) {
      // Clear previous selection when region changes
      setSelectedStationId('');
      setSelectedGeometry(null);
      setSelectedStation(null);
      setSelectedStationData(null);
      setSelectedCounty(''); // Clear county when region is selected
      
      // Trigger zoom to California
      setZoomToCaliforniaTrigger(prev => prev + 1);
      
      fetchStationsForSelectedRegion(selectedRegion);
    } else if (!selectedCounty) {
      // Only clear if county is also not selected
      setCountyStations([]);
      setMapLocations([]);
      setSelectedStationData(null);
    }
  }, [selectedRegion]);

  useEffect(() => {
    // Fetch stations when county is selected
    if (selectedCounty) {
      // Clear previous selection when county changes
      setSelectedStationId('');
      setSelectedGeometry(null);
      setSelectedStation(null);
      setSelectedStationData(null);
      setSelectedRegion(''); // Clear region when county is selected
      
      // Trigger zoom to California
      setZoomToCaliforniaTrigger(prev => prev + 1);
      
      fetchStationsForSelectedCounty(selectedCounty);
    } else if (!selectedRegion) {
      // Only clear if region is also not selected
      setCountyStations([]);
      setMapLocations([]);
      setSelectedStationData(null);
    }
  }, [selectedCounty]);

  useEffect(() => {
    // Fetch stations when region is selected
    if (selectedRegion) {
      // Clear previous selection when region changes
      setSelectedStationId('');
      setSelectedGeometry(null);
      setSelectedStation(null);
      setSelectedStationData(null);
      setSelectedCounty(''); // Clear county when region is selected
      
      fetchStationsForSelectedRegion(selectedRegion);
    } else if (!selectedCounty) {
      // Only clear if county is also not selected
      setCountyStations([]);
      setMapLocations([]);
      setSelectedStationData(null);
    }
  }, [selectedRegion]);


  useEffect(() => {
    // Update selected station when stations data changes
    if (selectedStation && stations.length > 0) {
      const updatedStation = stations.find(s => s.stationid === selectedStation.stationid);
      if (updatedStation) {
        setSelectedStation(updatedStation);
      }
    }
  }, [stations]);

  // Automatically show data modal when station data is loaded
  useEffect(() => {
    if (selectedStationData && selectedStationId) {
      setShowDataModal(true);
    }
  }, [selectedStationData, selectedStationId]);

  // Fetch station info when a station is selected
  const fetchStationInfo = async (masterid) => {
    try {
      setStationInfoLoading(true);
      const response = await apiServices.fetchStationInfo(masterid);
      
      if (response.success && response.results && response.results.length > 0) {
        setSelectedStationData(response.results[0]);
        return response.results[0];
      }
      return null;
    } catch (error) {
      console.error('Error fetching station info:', error);
      return null;
    } finally {
      setStationInfoLoading(false);
    }
  };

  const handleStationSelectorSelect = async (stationData) => {
    // Find the station's site geometry from countyStations
    const stationWithGeometry = countyStations.find(s => s.masterid === stationData.masterid);
    
    // Zoom to the station location if it has geometry
    if (stationWithGeometry?.site_geometry?.coordinates) {
      const [lng, lat] = stationWithGeometry.site_geometry.coordinates;
      setZoomToStation({ latitude: lat, longitude: lng });
      // Clear zoom state after a longer delay to ensure the effect runs
      setTimeout(() => setZoomToStation(null), 2000);
    } else {
      console.log('No geometry found for station');
    }
    
    // Fetch detailed station info for catchment polygon
    if (stationData.masterid) {
      const detailedInfo = await fetchStationInfo(stationData.masterid);
      if (detailedInfo) {
        setSelectedGeometry({
          ...stationData,
          site_geometry: stationWithGeometry?.site_geometry || null,
          catchment_geometry: detailedInfo.catchment_geometry,
          refstatus_final: stationWithGeometry?.refstatus_final || detailedInfo.refstatus_final
        });
      }
    } else {
      setSelectedGeometry({
        ...stationData,
        site_geometry: stationWithGeometry?.site_geometry || null,
        refstatus_final: stationWithGeometry?.refstatus_final
      });
    }
  };

  const handleStationClick = async (station) => {
    // Update the selected station ID to sync with dropdown
    if (station.stationid) {
      setSelectedStationId(station.stationid);
    }
    
    // Clear any pending zoom to prevent conflicts
    setZoomToStation(null);
    
    // Fetch detailed station info for catchment polygon
    if (station.masterid) {
      setStationInfoLoading(true);
      const detailedInfo = await fetchStationInfo(station.masterid);
      setStationInfoLoading(false);
      
      if (detailedInfo) {
        // Store the detailed station data (same as dropdown selection)
        setSelectedStationData(detailedInfo);
        
        setSelectedGeometry({
          ...station,
          site_geometry: station.site_geometry || null,
          catchment_geometry: detailedInfo.catchment_geometry
        });
      }
    }
    
    setSelectedStation(station);
    // Don't pan since user already clicked on the station they can see
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {isLoading ? (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="100vh"
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading data...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
          <Header title="Reference and Flow Status" />

          {/* Toolbar spacer - ensures content appears below fixed AppBar */}
          <Toolbar />
          
          {/* Map Content */}
          <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              {error ? (
                <Container maxWidth="md" sx={{ py: 4 }}>
                  <Alert severity="error">
                    <AlertTitle>Unable to Load Map Data</AlertTitle>
                    {error}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Please try refreshing the page or contact support if the problem persists.
                    </Typography>
                  </Alert>
                </Container>
              ) : (stations.length === 0 && counties.length === 0) ? (
                <Container maxWidth="md" sx={{ py: 4 }}>
                  <Alert severity="info">
                    <AlertTitle>No Station Data Available</AlertTitle>
                    There are currently no monitoring stations to display on the map. 
                    Please check back later or contact support for more information.
                  </Alert>
                </Container>
              ) : (
                <Box sx={{ 
                  height: '100%',
                  width: '100%',
                  position: 'relative'
                }}>
                  <CaliforniaMap
                    stations={filteredMapLocations}
                    onStationClick={handleStationClick}
                    zoomToStation={zoomToStation}
                    selectedGeometry={selectedGeometry}
                    isLoadingGeometry={stationInfoLoading}
                    selectedStationId={selectedStationId}
                    zoomToCaliforniaTrigger={zoomToCaliforniaTrigger}
                  />
                  
                  {/* Station Selector Modal Overlay */}
                  {(counties.length > 0 || regions.length > 0) && (
                    <StationSelectorModal
                      counties={counties}
                      regions={regions}
                      countyStations={countyStations}
                      onStationSelect={handleStationSelectorSelect}
                      selectedCounty={selectedCounty}
                      setSelectedCounty={setSelectedCounty}
                      selectedRegion={selectedRegion}
                      setSelectedRegion={setSelectedRegion}
                      selectedStationId={selectedStationId}
                      setSelectedStationId={setSelectedStationId}
                      fetchStationInfo={fetchStationInfo}
                      stationInfoLoading={stationInfoLoading}
                      stationsLoading={stationsLoading}
                      selectedStationData={selectedStationData}
                      onTabChange={() => setZoomToCaliforniaTrigger(prev => prev + 1)}
                      refStatusFilter={refStatusFilter}
                      setRefStatusFilter={setRefStatusFilter}
                      flowStatusFilter={flowStatusFilter}
                      setFlowStatusFilter={setFlowStatusFilter}
                    />
                  )}
                </Box>
              )}
          </Box>
          
          {/* Global Data Table Modal */}
          <DataTableModal
            open={showDataModal}
            onClose={() => setShowDataModal(false)}
            data={selectedStationData}
            stationId={selectedStationId}
          />


        </Box>
      )}
    </ThemeProvider>
  );
}

export default App;